import TextFontUpdater from 'client/components/core/Sidebar/components/Settings/components/Font/TextFontUpdater'
import {
  useUpdateEntity,
  useUpdatePropNew,
} from 'client/components/core/Sidebar/components/Settings/hooks/useUpdateProps'
import DeviceAppearanceNew from 'client/components/core/Sidebar/components/Settings/options/DeviceAppearance/DeviceAppearanceNew'
import HtmlAttrId from 'client/components/core/Sidebar/components/Settings/options/HtmlAttrId'
import {
  AlignStyle,
  FontSize,
} from 'client/components/core/Sidebar/components/Settings/styles'
import TextLineHeight from 'client/components/core/Sidebar/components/Settings/styles/TextLineHeight'
import { useDeviceModeContext } from 'client/context/DeviceModeContext'
import useManagement from 'client/hooks/useManagement'
import { getShownSearchResults } from 'client/reducers/managementReducer'
import { setShowSearchResults } from 'client/store/management/managementActions'
import React, { useCallback } from 'react'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'
import { useDispatch } from 'react-redux'
import { GroupTitle } from 'src/editor/layout/sidebar/settings/group-title'
import { SettingsNestedMenu } from 'src/editor/layout/sidebar/settings/settings-nested-menu'
import { ColorPickerControl } from 'src/editor/shared/settings/controls/color-picker/color-picker-control'
import { MarginGroup } from 'src/editor/shared/settings/groups/margin'
import { PaddingGroup } from 'src/editor/shared/settings/groups/padding'
import { SizeAndPositionGroup } from 'src/editor/shared/settings/groups/size-and-position'
import { Toggle } from 'src/editor/shared/ui-lib/toggle/toggle'
import { SearchInterface } from 'src/shared/types/entities/search-interface'
import { TextInputControl } from '../../settings/controls/text-input-control'
export interface SearchSettingsProps {
  entity: SearchInterface
}

export const SearchSettings = ({ entity }: SearchSettingsProps) => {
  const updateEntity = useUpdateEntity<SearchInterface>()
  const updateProp = useUpdatePropNew(entity)
  const { isMobile } = useDeviceModeContext()
  const { t } = useTypedTranslation()
  const dispatch = useDispatch()
  const { list: shownSearchResults } = useManagement(getShownSearchResults)
  const isShownSearchResult = shownSearchResults.includes(entity.htmlAttrId)

  const handleToggle = useCallback(
    (value: boolean) => {
      const id = entity.htmlAttrId

      const next = value
        ? Array.from(new Set([...shownSearchResults, id]))
        : shownSearchResults.filter((x: string) => x !== id)
      dispatch(setShowSearchResults(next))
    },
    [dispatch, shownSearchResults, entity.htmlAttrId],
  )
  return (
    <>
      <SettingsNestedMenu
        menuTitle={t('components.search.settings.dropdown_group.title')}
        menuButtonClassName="p-0 mb-2"
        menuButtonLabel={t('components.search.settings.dropdown_group.title')}
      >
        <Toggle
          onChange={handleToggle}
          checked={isShownSearchResult}
          labelClassName="justify-between w-full"
          startElement={t('components.search.settings.open_dropdown.label')}
        />
        {/* Dropdown result title */}
        <GroupTitle>
          {t('components.search.settings.dropdown_title.title')}
        </GroupTitle>
        <FontSize
          update={updateProp('itemTitle', 'fontSize')}
          mobileUpdate={updateProp('itemTitle', 'mobileFontSize')}
          fontSize={entity.itemTitle.fontSize}
          mobileFontSize={entity.itemTitle.mobileFontSize}
        />
        <TextLineHeight
          fontSize={entity.itemTitle.fontSize}
          mobileFontSize={entity.itemTitle.mobileFontSize}
          update={updateProp('itemTitle', 'lineHeight')}
          mobileUpdate={updateProp('itemTitle', 'mobileLineHeight')}
          lineHeight={entity.itemTitle.lineHeight}
          mobileLineHeight={entity.itemTitle.mobileLineHeight}
        />
        <TextFontUpdater<SearchInterface>
          entity={entity}
          update={updateEntity}
          nestedKey="itemTitle"
        />
        <AlignStyle
          align={
            isMobile
              ? entity.itemTitle.mobileTextAlign
              : entity.itemTitle.textAlign
          }
          update={updateProp(
            'itemTitle',
            isMobile ? 'mobileTextAlign' : 'textAlign',
          )}
          isFlex={false}
        />
        <ColorPickerControl
          update={updateProp('itemTitle', 'color')}
          mobileUpdate={updateProp('itemTitle', 'mobileColor')}
          color={entity.itemTitle.color}
          mobileColor={entity.itemTitle.mobileColor}
        />
        <MarginGroup
          label="settings_styles.margin.label"
          margin={
            isMobile ? entity.itemTitle.mobileMargin : entity.itemTitle.margin
          }
          update={updateProp('itemTitle', isMobile ? 'mobileMargin' : 'margin')}
        />
        {/* Dropdown result text */}
        <GroupTitle>
          {t('components.search.settings.dropdown_text.title')}
        </GroupTitle>
        <FontSize
          update={updateProp('itemText', 'fontSize')}
          mobileUpdate={updateProp('itemText', 'mobileFontSize')}
          fontSize={entity.itemText.fontSize}
          mobileFontSize={entity.itemText.mobileFontSize}
        />
        <TextLineHeight
          fontSize={entity.itemText.fontSize}
          mobileFontSize={entity.itemText.mobileFontSize}
          update={updateProp('itemText', 'lineHeight')}
          mobileUpdate={updateProp('itemText', 'mobileLineHeight')}
          lineHeight={entity.itemText.lineHeight}
          mobileLineHeight={entity.itemText.mobileLineHeight}
        />
        <TextFontUpdater<SearchInterface>
          entity={entity}
          update={updateEntity}
          nestedKey="itemText"
        />
        <AlignStyle
          align={
            isMobile
              ? entity.itemText.mobileTextAlign
              : entity.itemText.textAlign
          }
          update={updateProp(
            'itemText',
            isMobile ? 'mobileTextAlign' : 'textAlign',
          )}
          isFlex={false}
        />
        <ColorPickerControl
          update={updateProp('itemText', 'color')}
          mobileUpdate={updateProp('itemText', 'mobileColor')}
          color={entity.itemText.color}
          mobileColor={entity.itemText.mobileColor}
        />
        <MarginGroup
          label="settings_styles.margin.label"
          margin={
            isMobile ? entity.itemText.mobileMargin : entity.itemText.margin
          }
          update={updateProp('itemText', isMobile ? 'mobileMargin' : 'margin')}
        />
        {/* Dropdown see more */}
        <GroupTitle>
          {t('components.search.settings.dropdown_see_more.title')}
        </GroupTitle>

        <TextInputControl
          wrapperProps={{
            labelText: 'Text',
          }}
          value={entity.seeMore.text ?? ''}
          update={updateProp('seeMore', 'text')}
        />

        <FontSize
          update={updateProp('seeMore', 'fontSize')}
          mobileUpdate={updateProp('seeMore', 'mobileFontSize')}
          fontSize={entity.seeMore.fontSize}
          mobileFontSize={entity.seeMore.mobileFontSize}
        />
        <TextLineHeight
          fontSize={entity.seeMore.fontSize}
          mobileFontSize={entity.seeMore.mobileFontSize}
          update={updateProp('seeMore', 'lineHeight')}
          mobileUpdate={updateProp('seeMore', 'mobileLineHeight')}
          lineHeight={entity.seeMore.lineHeight}
          mobileLineHeight={entity.seeMore.mobileLineHeight}
        />
        <TextFontUpdater<SearchInterface>
          entity={entity}
          update={updateEntity}
          nestedKey="seeMore"
        />
        <AlignStyle
          align={
            isMobile ? entity.seeMore.mobileAlignSelf : entity.seeMore.alignSelf
          }
          update={updateProp(
            'seeMore',
            isMobile ? 'mobileAlignSelf' : 'alignSelf',
          )}
        />
        <ColorPickerControl
          update={updateProp('seeMore', 'color')}
          mobileUpdate={updateProp('seeMore', 'mobileColor')}
          color={entity.seeMore.color}
          mobileColor={entity.seeMore.mobileColor}
        />
        <PaddingGroup
          label="settings_styles.padding.label"
          padding={
            isMobile ? entity.seeMore.mobilePadding : entity.seeMore.padding
          }
          update={updateProp('seeMore', isMobile ? 'mobilePadding' : 'padding')}
        />
      </SettingsNestedMenu>

      <Toggle
        onChange={handleToggle}
        checked={isShownSearchResult}
        labelClassName="justify-between w-full"
        startElement={t('components.search.settings.open_dropdown.label')}
      />

      <GroupTitle>
        {t('components.search.settings.search_bar.title')}
      </GroupTitle>
      <TextInputControl
        wrapperProps={{
          labelText: t('components.search.settings.placeholder.label'),
        }}
        value={entity.input.placeholder ?? ''}
        update={updateProp('input', 'placeholder')}
      />

      <SizeAndPositionGroup
        margin={isMobile ? entity.mobileMargin : entity.margin}
        updateMargin={updateProp(isMobile ? 'mobileMargin' : 'margin')}
      />

      <DeviceAppearanceNew entity={entity} />
      <HtmlAttrId attrId={entity.htmlAttrId} />
    </>
  )
}
